import React, { useContext, useEffect, useLayoutEffect, useState, useRef } from 'react';
import { Slider, Slide, CarouselContext } from 'pure-react-carousel';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { Swipeable } from 'react-swipeable';
import StoryHolder from '../360Story/StoryHolder';
import CardHolder from '../../Common/CardHolder/CardHolder';
import StoryCarousel from '../StoryCarousel/StoryCarousel';
import QuoteStory from '../QuoteStory/QuoteStory';
import AudioClip from '../AudioClip/AudioClip';
import Podcast from '../Podcast/Podcast';
import Video from '../Video/Video';
import UserPoll from '../UserPoll/UserPoll';
import Scene3D from '../Scene3D/Scene3D';
import NextStory from '../../Common/NextStory/NextStory';
import { storyCardTypes } from '../../../models/Models';
import {
  setActiveSlideKey,
  setCurrentSlideDarkBg,
  setActiveSlideType,
  setHasVisitedStories,
} from '../../../store/reducers/mainReducer';
import { useCallback } from 'react';
import InfographicCarousel from '../InfographicCarousel/InfographicCarousel';
import { useWindowSize } from '../../../models/Hooks';
import { useMediaQuery } from 'react-responsive';

let maxDeltaY = 0;
let checkForScroll = true;

const SlideGenerator = ({ story, stories, id: storyId }) => {
  const cmsData = useSelector(state => state.data.cms);
  const [hidden, setHidden] = useState(false);
  const [showNextStory, setShowNextStory] = useState(false);
  const carouselContext = useContext(CarouselContext);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const hasVisitedPBP = useSelector(state => state.data.hasVisitedPBP);
  const exploreClicked = useSelector(state => state.data.exploreClicked);
  const mediaPlaying = useSelector(state => state.data.mediaPlaying);
  const activeCardType = useSelector(state => state.data.activeSlideType);
  const menuOpened = useSelector(state => state.data.menuOpened);
  const creditsOpened = useSelector(state => state.data.creditsOpened);
  const visitedStories = useSelector(state => state.data.visitedStories);
  const [nextStoryId, setNextStoryId] = useState(null);
  const [touchLocY, setTouchLocY] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const [isHorizontalScrolling, setIsHorizontalScrolling] = useState(false);
  const scene3DRef = useRef();
  const sliderHolderElement = useRef(null);
  const sliderElement = useRef(null);
  const dispatch = useDispatch();

    
  const isFullScreen =  window.innerHeight == window.screen.height;

  /* Handling new inline layout */
  const size = useWindowSize();
  const isLandscapeWithMargin = useMediaQuery({ query: '(min-width: 1274px)' });
  const isLandscapeNoMargin = useMediaQuery({
    query: '(orientation: landscape) and (max-width: 1274px)',
  });
  const aspectRatio = 2;
  const maxColumnWidth = 1234;
  const cardPadding = 30;
  const isInlineLayout = isLandscapeWithMargin || (isLandscapeNoMargin && !isFullScreen); // fullscreenElementHelper() ? false : 

  const cardWidth = isInlineLayout ? Math.min(maxColumnWidth, size.width - 32) : size.width;
  const cardHeight = isInlineLayout
    ? Math.min(size.height * 0.7, cardWidth / aspectRatio)
    : size.height;
  const topOffset = (size.height - cardHeight) / 2;
  const carouselWrapperStyle = isInlineLayout
    ? {
        position: 'relative',
        height: '100vh',
        overflowY: 'auto',
      }
    : null;
  const sliderStyle = isInlineLayout
    ? {
        top: topOffset + 'px',
        paddingBottom: topOffset + 'px',
      }
    : null;
  const cardStyle = isInlineLayout
    ? {
        position: 'relative',
        width: cardWidth + 'px',
        height: cardHeight + 'px',
        display: 'block',
        margin: cardPadding * 2 + 'px auto',
      }
    : null;
  const videoStyle = {
    position: 'relative',
    width: '100%',
    height: cardHeight + 'px',
    display: 'block',
    margin: 'auto',
    padding: cardPadding + 'px 0px',
  };

  var storyCards = [
    {
      Type: storyCardTypes.VIDEO_INTRO,
      Header: story.Header,
      Subheader: story.Subheader,
      IsDarkModePortrait: story.IsDarkMode,
      IsDarkModeLandscape: story.IsDarkMode,
      ...story.Introduction,
    },
    ...story.Cards,
  ];

  useEffect(() => {
    dispatch(setHasVisitedStories(true));
  }, []);

  useEffect(() => {
    const { currentSlide } = carouselContext.getStoreState();
    if (currentSlide !== carouselIndex) {
      setCarouselIndex(currentSlide);
    }
  }, [isInlineLayout]);

  useEffect(() => {
    if (carouselContext && storyCards.length && sliderHolderElement.current) {
      carouselContext.setStoreState({ currentSlide: 0, disableAnimation: true });
      setCarouselIndex(0);
      setActiveCard(storyCards[0]);
      setShowNextStory(false);
      sliderHolderElement.current.scrollTo(0, 0);
    }
  }, [storyId, carouselContext, isInlineLayout]);

  useEffect(() => {
    if (storyId < stories.length - 1) {
      setNextStoryId(storyId + 1);
    } else if (visitedStories.length === stories.length) {
      setNextStoryId(null);
    } else {
      visitedStories.some((id, i) => {
        if (id !== i) {
          setNextStoryId(i);
        }
        return id !== i;
      });
    }
  }, [stories, storyId, visitedStories]);

  useEffect(() => {
    const onChange = () => {
      const { currentSlide } = carouselContext.getStoreState();
      if (carouselIndex >= 0) {
        setCarouselIndex(currentSlide);
      }
    };
    carouselContext.subscribe(onChange);
    return () => carouselContext.unsubscribe(onChange);
  }, [carouselContext, dispatch, storyCards]);

  useEffect(() => {
    setActiveCard(storyCards[carouselIndex]);
  }, [carouselIndex]);


  const setActiveCard = currentCard => {
    dispatch(
      setCurrentSlideDarkBg(
        isInlineLayout ? currentCard.IsDarkModeLandscape : currentCard.IsDarkModePortrait
      )
    );
    dispatch(setActiveSlideKey(currentCard.Key));
    dispatch(setActiveSlideType(currentCard.Type));
  };

  const onGoToNextSlide = useCallback(() => {
    const { currentSlide } = carouselContext.getStoreState();
    console.log('next! carouselIndex: ', currentSlide);
    setShowNextStory(false);
    if (currentSlide < storyCards.length - 1) {
      carouselContext.setStoreState({ currentSlide: currentSlide + 1, disableAnimation: false });
    } else {
      setShowNextStory(true);
    }
  
  }, [storyCards, carouselContext]);

  const onGoToPrevSlide = useCallback(() => {
    
    const { currentSlide } = carouselContext.getStoreState();
    console.log('Prev! carouselIndex: ', currentSlide, carouselContext);
    setShowNextStory(false);
    if (currentSlide > 0) {
      carouselContext.setStoreState({ currentSlide: currentSlide - 1, disableAnimation: false });
    } else {
      console.log('first slide!');
    }
  
  }, [carouselContext]);

  useLayoutEffect(() => {
    const handleScrollInline = e => {
      const sliderRect = sliderElement.current.getBoundingClientRect();
      if (sliderRect && sliderRect.top && carouselContext) {
        var currentSlide = Math.round(Math.abs(sliderRect.top) / (cardHeight + topOffset / 2));
        if (currentSlide !== carouselIndex) {
          setCarouselIndex(currentSlide);
        }
        setShowNextStory(
          Math.abs(sliderRect.top) > sliderRect.height - (cardHeight + topOffset) - 200
        );
      }
    };
       
    const handleScroll = e => {
      e.preventDefault();
      e.stopPropagation();
      const scrollThreshold = 2;

     
      
      if (checkForScroll && Math.abs(e.deltaX) < Math.abs(e.deltaY)) {

        console.log("check for scroll")
        if (Math.abs(e.deltaY) > Math.abs(maxDeltaY) && Math.abs(maxDeltaY) <= scrollThreshold) {
          
          maxDeltaY = e.deltaY;
        } else {
          checkForScroll = false;
          if (Math.abs(maxDeltaY) > scrollThreshold && maxDeltaY > 0 && showNextStory) {
            setShowNextStory(false);
            maxDeltaY = 0;
            setTimeout(() => {
              checkForScroll = true;
            }, 1000);
            return;
          }
          if (Math.abs(maxDeltaY) > scrollThreshold) {
            if (maxDeltaY > 0) {
              if (activeCardType === storyCardTypes.OBJECT_3D) {
                scene3DRef.current && scene3DRef.current.handleSwipeUpRef();
              } else {
                onGoToNextSlide();

              }
            } else {
              if (activeCardType === storyCardTypes.OBJECT_3D) {
                scene3DRef.current && scene3DRef.current.handleSwipeDownRef();
              } else {

                onGoToPrevSlide();
              }
            }
          }
          maxDeltaY = 0;
          setTimeout(() => {
            checkForScroll = true;
            
          }, 1000);
        }
      }
    };

    // modern Chrome requires { passive: false } when adding event
    let supportsPassive = false;
    try {
      window.addEventListener(
        'test',
        null,
        Object.defineProperty({}, 'passive', {
          get: function () {
            supportsPassive = true;
          },
        })
      );
    } catch (e) {}

    const wheelOpt = supportsPassive ? { passive: false } : false;
    const wheelEvent = 'onwheel' in document.createElement('div') ? 'wheel' : 'mousewheel';

    const AddEvents = () => {
      window.addEventListener(wheelEvent, handleScroll, wheelOpt); // modern desktop
      window.addEventListener('touchmove', handleScroll, wheelOpt); // mobile
      window.addEventListener('keydown', handleScroll, false);

    };
    const RemoveEvents = () => {
      window.removeEventListener(wheelEvent, handleScroll);
      window.removeEventListener('touchmove', handleScroll);
      window.removeEventListener('keydown', handleScroll);
      window.removeEventListener('scroll', handleScrollInline, true); // modern desktop
    };

    if (isInlineLayout) {
      window.addEventListener('scroll', handleScrollInline, true); // modern desktop
    } else if (!menuOpened && !creditsOpened) {
      AddEvents();
    } else {
      RemoveEvents();
    }
    return () => {
      RemoveEvents();
    };
  }, [
    activeCardType,
    isInlineLayout,
    onGoToNextSlide,
    onGoToPrevSlide,
    showNextStory,
    menuOpened,
    creditsOpened,
  ]);

  useLayoutEffect(() => {
    const dotGroups = sliderHolderElement.current.getElementsByClassName('carousel__dot-group');
    for (let i = 0; i < dotGroups.length; i++) {
      const dotGroup = dotGroups[i];
      const j = i;
      setTimeout(() => {
        if (dotGroup.children && dotGroup.children.length) {
          dotGroup.children[0].click();
        }
      }, 50);
    }
  }, [story]);

  const handleSwipeUp = () => {
    if (activeCardType === storyCardTypes.OBJECT_3D) {
      scene3DRef.current && scene3DRef.current.handleSwipeUpRef();
    } else {
      console.log("[NEXT] is horizontal scrolling? ", isHorizontalScrolling)
      if(!isHorizontalScrolling){
        onGoToNextSlide();
      }
    }
  };

  const handleSwipeDown = () => {
    if (activeCardType === storyCardTypes.OBJECT_3D) {
      scene3DRef.current && scene3DRef.current.handleSwipeDownRef();
    } else {
      console.log("[PREV] is horizontal scrolling? ", isHorizontalScrolling)
      if(!isHorizontalScrolling){
        onGoToPrevSlide();
      }
    }
  };
  /**
   * Use in GetSlideFromStoryId
   * Return component from agreed id with JSON from CMS
   * @param {*} card The card data used to generate the component
   */
  const getHtmlFromStoryJSON = card => {
    let value;
    let id = card.Type;
    switch (id) {
      case storyCardTypes.VIDEO_INTRO:
        //video intro
        value = (
          <StoryHolder
            storyData={story}
            videoIntroData={card}
            topOffset={isInlineLayout ? topOffset + cardPadding : 0}
            isInline={isInlineLayout}
            size={{ width: size.width, height: cardHeight + topOffset + cardPadding }}
          />
        );
        break;
      case storyCardTypes.TEXT_CAROUSEL:
        value = (
          <StoryCarousel
            cardData={card}
            isInline={isInlineLayout}
            size={{ width: cardWidth, height: cardHeight }}
            preventScrolling={isScrolling}
            onScrollCallback={
             () => { setIsHorizontalScrolling(true)}}
             onScrollCompleteCallback={ () => {setIsHorizontalScrolling(false)}}
          />
        );
        break;
      case storyCardTypes.TEXT_CAROUSEL_WITH_BG:
        value = (
          <StoryCarousel
            cardData={card}
            isInline={isInlineLayout}
            size={{ width: cardWidth, height: cardHeight }}
            preventScrolling={isScrolling}
            onScrollCallback={
             () => { setIsHorizontalScrolling(true)}}
             onScrollCompleteCallback={ () => {setIsHorizontalScrolling(false)}}
          />
        );
        break;
      case storyCardTypes.TEXT_IMAGE_CAROUSEL:
        value = (
          <StoryCarousel
            cardData={card}
            isInline={isInlineLayout}
            size={{ width: cardWidth, height: cardHeight }}
            onScrollCallback={
             () => {setIsHorizontalScrolling(true)}}
             onScrollCompleteCallback={ () => {console.log("h scrolling done"); setIsHorizontalScrolling(false)}}
          />
        );
        break;
      case storyCardTypes.TEXT_VIDEO_CAROUSEL:
        //text & video carousel
        value = (
          <StoryCarousel
            cardData={card}
            isInline={isInlineLayout}
            size={{ width: cardWidth, height: cardHeight }}
            preventScrolling={isScrolling}
            onScrollCallback={
             () => {setIsHorizontalScrolling(true)}}
             onScrollCompleteCallback={ () => {console.log("h scrolling done"); setIsHorizontalScrolling(false)}}

          />
        );
        break;
      case storyCardTypes.MIXED_MEDIA_CAROUSEL:
        value = (
          <StoryCarousel
            cardData={card}
            isInline={isInlineLayout}
            size={{ width: cardWidth, height: cardHeight }}
            preventScrolling={isScrolling}
            onScrollCallback={
             () => { setIsHorizontalScrolling(true)}}
             onScrollCompleteCallback={ () => {setIsHorizontalScrolling(false)}}
          />
        );
        break;
      case storyCardTypes.INFOGRAPHIC_CAROUSEL:
        value = <InfographicCarousel cardData={card} isInline={isInlineLayout} />;
        break;
      case storyCardTypes.OBJECT_3D:
        value = (
          <Scene3D
            cardData={card}
            onGoToNextSlide={onGoToNextSlide}
            onGoToPrevSlide={onGoToPrevSlide}
            ref={scene3DRef}
            isInline={isInlineLayout}
            size={{ width: cardWidth, height: cardHeight }}
          />
        );
        break;
      case storyCardTypes.QUOTE:
        value = <QuoteStory cardData={card} isInline={isInlineLayout} />;
        break;
      case storyCardTypes.VIDEO:
        //video
        value = (
          <Video
            cardData={card}
            isInline={isInlineLayout}
            size={{ width: cardWidth, height: cardHeight }}
          />
        );
        break;
      case storyCardTypes.AUDIO_PODCAST:
        //audio podcast
        value = <AudioClip
          cardData={card}
          isInline={isInlineLayout}
          size={{ width: cardWidth, height: cardHeight }}
        />;
        break;
      case storyCardTypes.AUDIO_MISC:
        //audio misc
        value = <AudioClip
          cardData={card}
          isInline={isInlineLayout}
          size={{ width: cardWidth, height: cardHeight }}
        />;
        break;
      case storyCardTypes.USER_POLL:
        //user poll
        value = <UserPoll cardData={card} isInline={isInlineLayout} />;
        break;
      default:
        console.log('No matching ID for component. See StorySelector.js');
        break;
    }

    return value;
  };

  const mapSlidesToJSON = cards => {
    var value;
    cards.length > 0
      ? (value = cards.map((card, index) => {
          return (
            <Slide
              index={index}
              key={index}
              style={isInlineLayout ? (index > 0 ? cardStyle : videoStyle) : null}              
            onTouchStart={(e) => {setTouchLocY(e.touches[0].clientY)}}
            onTouchMove={(e) => {if(Math.abs(e.touches[0].clientY - touchLocY ) > 50) setIsScrolling(true); else setIsScrolling(false);}}//vertical travel distance of 100px
            onTouchEnd={() => {setIsScrolling(false);}}
            >
              <CardHolder
                story={story}
                currentIndex={index}
                showProgressBar={card.Type !== storyCardTypes.VIDEO_INTRO}
                onArrowClicked={onGoToNextSlide}
                index={index}
                hasArrow={
                  (index > 0 || exploreClicked) &&
                  index < cards.length - 1 &&
                  mediaPlaying === false
                }
                isLightBg={isInlineLayout ? !card.IsDarkModeLandscape : !card.IsDarkModePortrait}
                isInlineCard={isInlineLayout}
                cardHeight={cardHeight}
                active={index==carouselIndex}

                
              >
                {getHtmlFromStoryJSON(card)}
              </CardHolder>
            </Slide>
          );
        }))
      : console.log('No data given');
    return value;
  };

  return (
    <main className={`${hidden ? 'hidden' : ''} ${isInlineLayout ? 'inline-wrap' : ''}`}>
      <Swipeable

        trackTouch={!isInlineLayout}
        trackMouse={!isInlineLayout}
        onSwipedDown={!isInlineLayout ? handleSwipeDown : null}
        onSwipedUp={!isInlineLayout ? handleSwipeUp : null}
      >
        <div style={carouselWrapperStyle} ref={sliderHolderElement}>
          <div className="slider-ref" ref={sliderElement}>
            <Slider
              moveThreshold={0.1}
              classNameAnimation={`slow-carousel`}
              style={sliderStyle}
              ref={sliderElement}
            >
              {mapSlidesToJSON(storyCards)}
            </Slider>
          </div>
        </div>
      </Swipeable>
      {nextStoryId !== null ? (
        <NextStory
          showCredits={showNextStory}
          showNext={showNextStory}
          next={`/${cmsData.digitalMuseum.Path.Text}/${stories[nextStoryId].Menu.Path}`}
          title={stories[nextStoryId].Header}
          subtitle={stories[nextStoryId].Subheader}
          onCreditsClick={()=>{}}
        />
      ):
        <NextStory
          showCredits={showNextStory}
          showNext={showNextStory}
          next={hasVisitedPBP ? `/${cmsData.poweredByPurpose.Path.Text}/${cmsData.poweredByPurpose.Introduction.Menu.Path}` : `/${cmsData.buildingBrighterFutures.Path.Text}/${cmsData.buildingBrighterFutures.Introduction.Menu.Path}`}
          title={hasVisitedPBP ? cmsData.global.BottomNavbar.UpNext.PBP.Title : cmsData.global.BottomNavbar.UpNext.BBF.Title }
          subtitle={hasVisitedPBP ? cmsData.global.BottomNavbar.UpNext.PBP.Subtitle : cmsData.global.BottomNavbar.UpNext.BBF.Subtitle }
          onCreditsClick={()=>{}}
        />
      }
    </main>
  );
};

export default SlideGenerator;
